/**
 * M31 task-16 — pure helpers behind the /admin/settings Configuration section
 * (HUB-092 §5). Extracted so the no-jsdom vitest suite can cover grouping,
 * labels, input kinds, and PUT payload parsing without a DOM harness. Must NOT
 * import `@/lib/hub-client` (directly or transitively) — same rule as
 * `format.ts`.
 *
 * The SERVER list (GET /settings/config) is the source of truth for WHICH
 * keys exist — nothing here enumerates the registry to render from. The
 * shared `HubSettingValueSchemas` single-home (the exact registry the hub
 * validates against) is consulted only to pick the right input control per
 * key, with a value-shape fallback so a key the SPA doesn't know yet still
 * renders.
 */
import type { Static } from '@verybigthings/trackframe-shared/schemas';
import {
  HubSettingDescriptions,
  HubSettingValueSchemas,
  type HubSettingEntrySchema,
} from '@verybigthings/trackframe-shared/schemas';

/** One resolved registry entry as GET /settings/config returns it. */
export type HubSettingEntry = Static<typeof HubSettingEntrySchema>;

// ---- row identity ----

/**
 * React key for a settings row: value + source in it remount the row (and
 * its drafts) when a save, reset or refresh lands a new effective value.
 * Objects are JSON (`String` gives "[object Object]" for all of them, so
 * the row never remounted); primitives keep the `String` form.
 */
export function rowRemountKey(entry: { key: string; value?: unknown; source: string }): string {
  const value =
    typeof entry.value === 'object' && entry.value !== null ? JSON.stringify(entry.value) : String(entry.value);
  return `${entry.key}|${value}|${entry.source}`;
}

// ---- grouping ----

/** Fixed order of the known families; unknown ones append in first-seen order. */
const FAMILY_ORDER = [
  'schedules', 'memory', 'audit', 'recall', 'ingestion', 'web',
  'connectors', 'db_connectors', 'agent', 'auth', 'skills', 'email',
  // COP-148 D7 — appended at the END; the order above is the order operators
  // have learned, so a new family joins it rather than reshuffling it.
  'approvals',
  'portal', // HUB-125 — appended, same rule.
];

export interface SettingsGroup {
  /** Family id — the key prefix before the first dot. */
  family: string;
  /** Heading label ("schedules" → "Schedules"). */
  label: string;
  entries: HubSettingEntry[];
}

/** Family of a key: the prefix before the first `.` (dotless keys group as-is). */
export function familyOf(key: string): string {
  const dot = key.indexOf('.');
  return dot === -1 ? key : key.slice(0, dot);
}

/** Group the server's entry list by family, known families first, in order. */
export function groupSettings(entries: HubSettingEntry[]): SettingsGroup[] {
  const byFamily = new Map<string, HubSettingEntry[]>();
  for (const entry of entries) {
    const family = familyOf(entry.key);
    const bucket = byFamily.get(family);
    if (bucket) bucket.push(entry);
    else byFamily.set(family, [entry]);
  }
  const known = FAMILY_ORDER.filter((family) => byFamily.has(family));
  const unknown = [...byFamily.keys()].filter((family) => !FAMILY_ORDER.includes(family));
  return [...known, ...unknown].map((family) => ({
    family,
    label: capitalize(family),
    entries: byFamily.get(family)!,
  }));
}

/** "db_connectors" → "Db connectors"; single-word families are unchanged. */
function capitalize(text: string): string {
  const spaced = text.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// ---- labels ----

/**
 * OPTIONAL label overrides — the only place allowed to name keys. Everything
 * not listed here falls back to the derived label below, so a new key never
 * renders blank.
 */
const LABEL_OVERRIDES: Record<string, string> = {
  'email.smtp_host': 'SMTP host',
  'email.smtp_port': 'SMTP port',
  'email.smtp_user': 'SMTP user',
  'email.smtp_pass': 'SMTP password',
  'email.smtp_from': 'SMTP from address',
  'schedules.auto_pause_denial_threshold': 'Auto-pause denial threshold',
  'web.search_cost_tokens': 'Search cost (token-equivalents)',
  'web.extract_cost_tokens': 'Extract cost (token-equivalents)',
  'web.nav_action_cost_tokens': 'Nav action cost (token-equivalents)',
  'web.session_ttl_ms': 'Nav session lifetime (ms)',
  'schedules.whole_plan_preapproval_enabled': 'Whole-plan preapproval enabled',
  'portal.landing_url': 'Landing URL after sign-in',
};

/** Unit-ish trailing tokens rendered in parentheses: `runner_tick_ms` → "Runner tick (ms)". */
const UNIT_SUFFIXES: Record<string, string> = { ms: 'ms', days: 'days', utc: 'UTC' };

/** Human label for a key: override map first, else derived from the key itself. */
export function labelFor(key: string): string {
  const override = LABEL_OVERRIDES[key];
  if (override) return override;
  const name = key.includes('.') ? key.slice(key.indexOf('.') + 1) : key;
  const words = name.split('_').filter(Boolean);
  if (words.length === 0) return key;
  const unit = words.length > 1 ? UNIT_SUFFIXES[words[words.length - 1]] : undefined;
  if (unit) words.pop();
  return capitalize(words.join(' ')) + (unit ? ` (${unit})` : '');
}

/**
 * Plain-language description for a key, from the shared registry. Returns
 * `undefined` for a key this SPA build does not know (a newer hub), so the row
 * renders without help text rather than breaking — the same
 * forward-compatibility rule `valueKindFor` follows.
 */
export function descriptionFor(key: string): string | undefined {
  return (HubSettingDescriptions as Record<string, string | undefined>)[key];
}

// ---- input kinds ----

export type ValueKind =
  | { kind: 'boolean' }
  | { kind: 'number'; integer: boolean }
  | { kind: 'select'; options: string[] }
  | { kind: 'string' }
  // HUB-127 — an object of optional integers with no extra keys (e.g.
  // `audit.retention_days_by_source`): one number field per key.
  | { kind: 'int_map'; keys: string[]; min: number }
  // HUB-127 — any other object-valued key, edited as JSON text.
  | { kind: 'json' };

export type IntMapKind = Extract<ValueKind, { kind: 'int_map' }>;

// Registry schemas are plain JSON-schema-shaped objects at runtime; this is
// the loose read surface for picking a control (never for validating — the
// server owns validation).
interface LooseSchema {
  type?: string;
  anyOf?: Array<{ const?: unknown }>;
  // A single-member Type.Union collapses to the literal itself at runtime.
  const?: unknown;
  // Plain JSON-schema enums (an extension may declare `Type.Enum`, HUB-103).
  enum?: unknown[];
  // Object schemas (HUB-127).
  additionalProperties?: unknown;
  properties?: Record<string, { type?: string; minimum?: number }>;
}

/**
 * Input kind for a key, from the shared registry schema. A key absent from
 * the local registry (a hub newer than this SPA build) falls back to the
 * shape of its current value.
 */
export function valueKindFor(key: string, currentValue?: unknown): ValueKind {
  const schema = (HubSettingValueSchemas as Record<string, LooseSchema>)[key];
  if (!schema) return kindFromValue(currentValue);
  return kindFromJsonSchema(schema);
}

/**
 * Input kind from a JSON schema object — the hub registry's or one an
 * extension ships over the wire (HUB-103 `schema` field). Anything
 * unrecognised renders as free text; the server owns validation.
 */
export function kindFromJsonSchema(raw: unknown): ValueKind {
  if (typeof raw !== 'object' || raw === null) return { kind: 'string' };
  const schema = raw as LooseSchema;
  if (Array.isArray(schema.anyOf)) {
    const options = schema.anyOf
      .map((member) => member.const)
      .filter((c): c is string => typeof c === 'string');
    if (options.length > 0) return { kind: 'select', options };
  }
  if (Array.isArray(schema.enum)) {
    const options = schema.enum.filter((c): c is string => typeof c === 'string');
    if (options.length > 0) return { kind: 'select', options };
  }
  if (typeof schema.const === 'string') return { kind: 'select', options: [schema.const] };
  if (schema.type === 'boolean') return { kind: 'boolean' };
  if (schema.type === 'integer') return { kind: 'number', integer: true };
  if (schema.type === 'number') return { kind: 'number', integer: false };
  if (schema.type === 'object') return intMapKind(schema) ?? { kind: 'json' };
  return { kind: 'string' };
}

/** `int_map` for an object of integers with no extra keys; undefined otherwise. */
function intMapKind(schema: LooseSchema): IntMapKind | undefined {
  const properties = Object.values(schema.properties ?? {});
  if (schema.additionalProperties !== false || properties.length === 0) return undefined;
  if (!properties.every((property) => property.type === 'integer')) return undefined;
  const minimums = properties
    .map((property) => property.minimum)
    .filter((min): min is number => typeof min === 'number');
  return {
    kind: 'int_map',
    keys: Object.keys(schema.properties!),
    min: minimums.length > 0 ? Math.max(...minimums) : 0,
  };
}

function kindFromValue(value: unknown): ValueKind {
  if (typeof value === 'boolean') return { kind: 'boolean' };
  if (typeof value === 'number') return { kind: 'number', integer: Number.isInteger(value) };
  if (typeof value === 'object' && value !== null) return { kind: 'json' };
  return { kind: 'string' };
}

// ---- PUT payload ----

export type PutBodyResult =
  | { ok: true; body: { value: unknown } }
  | { ok: false; message: string };

/**
 * `{ value }` for PUT /settings/config/:key with the right primitive type per
 * input kind. Number parsing fails soft (inline message) instead of sending
 * an unserializable `NaN`; JSON text must parse to a plain object (HUB-127);
 * everything else is validated server-side.
 */
export function buildPutBody(kind: ValueKind, raw: boolean | string): PutBodyResult {
  if (kind.kind === 'boolean') {
    const value = typeof raw === 'boolean' ? raw : raw === 'true';
    return { ok: true, body: { value } };
  }
  if (kind.kind === 'number') {
    const text = String(raw).trim();
    const value = Number(text);
    if (text === '' || !Number.isFinite(value)) return { ok: false, message: 'Enter a number.' };
    if (kind.integer && !Number.isInteger(value)) {
      return { ok: false, message: 'Enter a whole number.' };
    }
    return { ok: true, body: { value } };
  }
  if (kind.kind === 'json') return parseJsonObject(String(raw));
  if (kind.kind === 'int_map') return parseIntMap(kind, String(raw));
  return { ok: true, body: { value: String(raw) } };
}

/** `int_map`: the control's assembled object; keys ⊆ `kind.keys`, integers ≥ `kind.min`. */
function parseIntMap(kind: IntMapKind, text: string): PutBodyResult {
  const parsed = parseJsonObject(text);
  if (!parsed.ok) return parsed;
  for (const [key, value] of Object.entries(parsed.body.value as Record<string, unknown>)) {
    if (!kind.keys.includes(key)) return { ok: false, message: 'Unknown source.' };
    if (!Number.isInteger(value) || (value as number) < kind.min) {
      return { ok: false, message: 'Enter a whole number of days.' };
    }
  }
  return parsed;
}

function parseJsonObject(text: string): PutBodyResult {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return { ok: false, message: 'Enter valid JSON.' };
  }
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return { ok: false, message: 'Enter a JSON object.' };
  }
  return { ok: true, body: { value } };
}

// ---- int_map control (HUB-127) ----

/** Draft text per key: the stored number, else '' (blank = use the general window). */
export function intMapDrafts(kind: IntMapKind, value: unknown): Record<string, string> {
  const stored = typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
  return Object.fromEntries(
    kind.keys.map((key) => [key, typeof stored[key] === 'number' ? String(stored[key]) : '']),
  );
}

/**
 * The raw string `IntMapInput` saves on blur: an object of every NON-blank
 * field, as JSON. `null` when it equals the stored value (nothing to save).
 */
export function intMapCommitRaw(
  kind: IntMapKind,
  value: unknown,
  drafts: Record<string, string>,
): string | null {
  const raw = JSON.stringify(assembleIntMap(kind, drafts));
  return raw === JSON.stringify(assembleIntMap(kind, intMapDrafts(kind, value))) ? null : raw;
}

/** `{ key: Number(text) }` for every non-blank draft, in `kind.keys` order. */
function assembleIntMap(kind: IntMapKind, drafts: Record<string, string>): Record<string, number> {
  const assembled: Record<string, number> = {};
  for (const key of kind.keys) {
    const text = (drafts[key] ?? '').trim();
    if (text !== '') assembled[key] = Number(text);
  }
  return assembled;
}

// ---- error copy ----

/**
 * Human message for a failed write. Prefers the per-error `details` paths a
 * `validation_error` body carries, then the body `message`, then the fallback.
 */
export function putErrorMessage(body: unknown, fallback: string): string {
  const record = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  if (Array.isArray(record.details)) {
    const parts = record.details
      .filter((d): d is { path?: unknown; message?: unknown } => Boolean(d) && typeof d === 'object')
      .map((d) => {
        const message = typeof d.message === 'string' ? d.message : '';
        const path = typeof d.path === 'string' && d.path !== '' ? `${d.path}: ` : '';
        return message ? `${path}${message}` : '';
      })
      .filter(Boolean);
    if (parts.length > 0) return parts.join('; ');
  }
  if (typeof record.message === 'string' && record.message !== '') return record.message;
  return fallback;
}
